import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Building2, Calendar, Clock, MapPin, Play, CheckCircle, Plus, AlertTriangle, Camera, FileText, User, ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';
import CreateAnomalieModal from './CreateAnomalieModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  'planifiee': 'bg-blue-100 text-blue-800',
  'en_cours': 'bg-yellow-100 text-yellow-800',
  'terminee': 'bg-green-100 text-green-800',
  'validee': 'bg-emerald-100 text-emerald-800',
  'annulee': 'bg-gray-100 text-gray-800'
};

const statusLabels = {
  'planifiee': 'Planifiée',
  'en_cours': 'En cours',
  'terminee': 'Terminée',
  'validee': 'Validée',
  'annulee': 'Annulée'
};

export default function VisiteDetail({ visiteId, onBack, token }) {
  const [visite, setVisite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnomalieModal, setShowAnomalieModal] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchVisite = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/visites/${visiteId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVisite(data);
      } else {
        toast.error('Erreur lors du chargement de la visite');
      }
    } catch (error) {
      console.error('Error fetching visite:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [visiteId, token]);

  useEffect(() => {
    fetchVisite();
  }, [fetchVisite]);

  const handleStartVisite = async () => {
    try {
      const response = await fetch(`${API_URL}/api/visites/${visiteId}/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Visite démarrée');
        fetchVisite();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors du démarrage');
    }
  };

  const handleFinishVisite = async () => {
    try {
      const response = await fetch(`${API_URL}/api/visites/${visiteId}/finish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          resume: `Visite terminée le ${new Date().toLocaleDateString('fr-FR')}`,
          zones_visitees: visite?.building?.zones?.map(z => z.id) || []
        })
      });

      if (response.ok) {
        toast.success('Visite terminée');
        fetchVisite();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la finalisation');
    }
  };

  const handleCreateAnomalie = async (anomalieData) => {
    try {
      const response = await fetch(`${API_URL}/api/visites/${visiteId}/anomalies`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...anomalieData,
          building_id: visite?.building_id
        })
      });

      if (response.ok) {
        toast.success('Anomalie signalée');
        setShowAnomalieModal(false);
        fetchVisite();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div>
      </div>
    );
  }

  if (!visite) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Visite non trouvée</p>
        <button onClick={onBack} className="mt-4 text-[#00D3A8] hover:underline">
          Retour à la liste
        </button>
      </div>
    );
  }

  const zones = visite?.building?.zones || [];
  const anomalies = visite?.anomalies || [];
  const canModify = visite.statut !== 'terminee' && visite.statut !== 'validee';

  return (
    <div className="p-6" data-testid="visite-detail">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          Retour aux visites
        </button>

        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">{visite.titre}</h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[visite.statut]}`}>
                {statusLabels[visite.statut]}
              </span>
            </div>
            <div className="flex items-center gap-4 text-gray-500">
              <span className="flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                {visite.building?.nom || 'Immeuble'}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(visite.date_planifiee)}
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            {visite.statut === 'planifiee' && (
              <button
                onClick={handleStartVisite}
                className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00BFA6] transition-colors"
              >
                <Play className="h-5 w-5" />
                Démarrer la visite
              </button>
            )}
            {visite.statut === 'en_cours' && (
              <>
                <button
                  onClick={() => setShowAnomalieModal(true)}
                  className="flex items-center gap-2 px-4 py-2 border border-orange-500 text-orange-500 rounded-lg hover:bg-orange-50 transition-colors"
                >
                  <AlertTriangle className="h-5 w-5" />
                  Signaler anomalie
                </button>
                <button
                  onClick={handleFinishVisite}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  <CheckCircle className="h-5 w-5" />
                  Terminer
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {['overview', 'zones', 'anomalies'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium transition-colors relative ${
              activeTab === tab
                ? 'text-[#00D3A8]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'overview' && 'Vue d\'ensemble'}
            {tab === 'zones' && `Zones (${zones.length})`}
            {tab === 'anomalies' && `Anomalies (${anomalies.length})`}
            {activeTab === tab && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00D3A8]"></div>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'overview' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Info Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Informations</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Immeuble</p>
                  <p className="font-medium">{visite.building?.nom || '-'}</p>
                  <p className="text-sm text-gray-500">{visite.building?.adresse || '-'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Date planifiée</p>
                  <p className="font-medium">{formatDate(visite.date_planifiee)}</p>
                </div>
              </div>
              {visite.date_debut && (
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Démarrée le</p>
                    <p className="font-medium">{formatDate(visite.date_debut)}</p>
                  </div>
                </div>
              )}
              {visite.date_fin && (
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Terminée le</p>
                    <p className="font-medium">{formatDate(visite.date_fin)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Stats Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Statistiques</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{zones.length}</p>
                <p className="text-sm text-blue-700">Zones à visiter</p>
              </div>
              <div className={`rounded-lg p-4 text-center ${anomalies.length > 0 ? 'bg-orange-50' : 'bg-green-50'}`}>
                <p className={`text-3xl font-bold ${anomalies.length > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                  {anomalies.length}
                </p>
                <p className={`text-sm ${anomalies.length > 0 ? 'text-orange-700' : 'text-green-700'}`}>
                  Anomalies
                </p>
              </div>
            </div>
            {visite.description && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500 mb-1">Description</p>
                <p className="text-gray-700">{visite.description}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'zones' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          {zones.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucune zone définie pour cet immeuble</p>
              <p className="text-sm text-gray-400 mt-1">
                Les zones sont configurées dans la fiche de l'immeuble
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {zones.map(zone => (
                <div
                  key={zone.id}
                  className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => setSelectedZone(zone)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <MapPin className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{zone.nom}</p>
                        <p className="text-sm text-gray-500">
                          {zone.type} {zone.etage ? `• Étage ${zone.etage}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {zone.equipements?.length > 0 && (
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-600">
                          {zone.equipements.length} équipement(s)
                        </span>
                      )}
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'anomalies' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          {canModify && (
            <div className="p-4 border-b border-gray-100">
              <button
                onClick={() => setShowAnomalieModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                <Plus className="h-5 w-5" />
                Signaler une anomalie
              </button>
            </div>
          )}
          {anomalies.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucune anomalie signalée</p>
              {canModify && (
                <button
                  onClick={() => setShowAnomalieModal(true)}
                  className="mt-4 text-orange-500 hover:underline"
                >
                  Signaler une anomalie
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {anomalies.map(anomalie => (
                <div key={anomalie.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      anomalie.priorite === 'critique' ? 'bg-red-100' :
                      anomalie.priorite === 'haute' ? 'bg-orange-100' :
                      anomalie.priorite === 'moyenne' ? 'bg-yellow-100' : 'bg-gray-100'
                    }`}>
                      <AlertTriangle className={`h-5 w-5 ${
                        anomalie.priorite === 'critique' ? 'text-red-600' :
                        anomalie.priorite === 'haute' ? 'text-orange-600' :
                        anomalie.priorite === 'moyenne' ? 'text-yellow-600' : 'text-gray-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900">{anomalie.titre}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          anomalie.status === 'Nouveau' ? 'bg-blue-100 text-blue-800' :
                          anomalie.status === 'En cours' ? 'bg-yellow-100 text-yellow-800' :
                          anomalie.status === 'Résolu' ? 'bg-green-100 text-green-800' : 'bg-gray-100'
                        }`}>
                          {anomalie.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{anomalie.description}</p>
                      {anomalie.zone_nom && (
                        <p className="text-xs text-gray-400 mt-1">
                          <MapPin className="h-3 w-3 inline mr-1" />
                          {anomalie.zone_nom}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Zone Detail Modal */}
      {selectedZone && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">{selectedZone.nom}</h3>
              <button
                onClick={() => setSelectedZone(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">Type</p>
                  <p className="font-medium">{selectedZone.type}</p>
                </div>
                {selectedZone.etage && (
                  <div>
                    <p className="text-sm text-gray-500">Étage</p>
                    <p className="font-medium">{selectedZone.etage}</p>
                  </div>
                )}
                {selectedZone.description && (
                  <div>
                    <p className="text-sm text-gray-500">Description</p>
                    <p className="font-medium">{selectedZone.description}</p>
                  </div>
                )}
                {selectedZone.equipements?.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Équipements</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedZone.equipements.map((eq, i) => (
                        <span key={i} className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                          {eq.nom || eq}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {canModify && (
                <button
                  onClick={() => {
                    setSelectedZone(null);
                    setShowAnomalieModal(true);
                  }}
                  className="w-full mt-6 flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  <AlertTriangle className="h-5 w-5" />
                  Signaler une anomalie dans cette zone
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Anomalie Modal */}
      {showAnomalieModal && (
        <CreateAnomalieModal
          zones={zones}
          onClose={() => setShowAnomalieModal(false)}
          onCreate={handleCreateAnomalie}
        />
      )}
    </div>
  );
}
